export type BiEventName =
  | UI_EVENTS
  | GET_STARTED_EVENTS
  | SIGN_UP_EVENTS
  | ACTIONS_EVENTS
  | PAGE_VISIT_EVENTS
  | PAYWALL_EVENTS
  | PAGEBLOCK_EVENTS
  | USER_EVENTS
  | HOMEPAGE_EVENTS
  | AI_AGENTS_EVENTS
  | MARKETING_ATTRIBUTION_EVENTS;

export type BiEvent = { name: BiEventName; props?: Record<string, any> };

export enum USER_EVENTS {
  meetingBooked = 'Meeting Booked',
  meetingConfirmed = 'Meeting Confirmed',
}

export enum UI_EVENTS {
  documentationOpenCard = 'documentation:open_card',
  documentationShowMore = 'documentation:show_more',
  documentationAiEngage = 'documentation:ai_engage',
  documentationSearchStarted = 'documentation:search_started',
  documentationSearchResultsPresented = 'documentation:search_results_presented',
  documentationAddMetadataBrowseTab = 'documentation:add_metadata_browse_tab',
  documentationAddMetadataToCanvas = 'documentation:add_metadata_to_canvas',
  automationsOpen = 'automations:open_card',
  alertsOpen = 'alerts:open_card',
  assignmentsOpen = 'assignments:open_card',
  matchingDedupeOpen = 'matching_dedupe:open_card',
  scheduledAssignmentsOpen = 'scheduled_assignments:open_card',
  scheduledReportsOpen = 'scheduled_reports:open_card',
  scheduledJobsOpen = 'scheduled_jobs:open_card',
  marketingAttributionModelsOpen = 'marketing_attribution:open_card',
  funnelsOpen = 'funnels:open_canvas',
  rollupsOpen = 'rollups:open_card',
  completeSetupOpen = 'complete_setup:open_card',
  canvasOpenDocumentation = 'canvas:open_documentation',
  canvasOpenAutomation = 'canvas:open_automation',
  canvasOpenAlerts = 'canvas:open_alerts',
  canvasOpenTemplates = 'canvas:open_templates',
  canvasCommentMode = 'canvas:comment_mode',
  canvasAddMetadataToCanvas = 'canvas:add_metadata_to_canvas',
  openHelpCenter = 'visit_pages:help_center',
  connectToSfClick = 'connect_to_sf_click',
}

export enum PAYWALL_EVENTS {
  trialActivated = 'trial_activated',
  paywall = 'paywall',
}

export enum PAGEBLOCK_EVENTS {
  bookDemoClick = 'book_demo_click',
  openCtaDialogClick = 'demo_CTA_click',
  connectToSfClick = 'connect_to_sf_click',
  scheduleMeetingClick = 'schedule_meeting_click',
  chatWithUsClick = 'chat_with_us_click',
}

export enum ACTIONS_EVENTS {
  inviteUser = 'actions:invite_user',
  panelStateChange = 'actions:panel_state_change',
  documentationAddObject = 'documentation:add_object',
  documentationAddTag = 'documentation:add_tag',
  documentationRemoveTag = 'documentation:remove_tag',
  documentationAddOwner = 'documentation:add_owner',
  documentationRemoveOwner = 'documentation:remove_owner',
  documentationAddCollaborator = 'documentation:add_collaborator',
  documentationRemoveCollaborator = 'documentation:remove_collaborator',
  documentationExportCsv = 'documentation:export_csv',
  funnelsAddFunnel = 'funnels:add_funnel',
  funnelsAddThirdParty = 'funnels:add_3rdparty_funnel',
  addComment = 'add_comment',
  slackConnect = 'integrations:connect_slack',
  teamsConnect = 'integrations:connect_teams',
  visualizeRecordType = 'visualize_record_type',
  changeFeedAddNotification = 'change_feed:add_notification',
  automationsAdd = 'automations:add_automation',
  alertsAdd = 'alerts:add_alert',
  assignmentsAdd = 'assignments:add_assignment',
  matchingDedupeAdd = 'matching_dedupe:add_dm',
  assignmentGroupsAdd = 'assignment_groups:add_group',
  gatesAdd = 'gates:add_gate',
  scheduledAssignmentsAdd = 'scheduled_assignments:add_scheduled_assignment',
  territoriesAdd = 'territories:add_territory',
  rollupsAdd = 'rollups:add_rollup',
  marketingAttributionModelAdd = 'campaign_attribution_model:add_model',
  automationsDeploySuccess = 'automations:deploy_success',
  alertsDeploySuccess = 'alerts:deploy_success',
  assignmentsDeploySuccess = 'assignments:deploy_success',
  matchingDedupeDeploySuccess = 'matching_dedupe:deploy_success',
  scheduledAssignmentsDeploySuccess = 'scheduled_assignments:deploy_success',
  rollupsDeploySuccess = 'rollups:deploy_success',
  funnelsDeploySuccess = 'funnels:deploy_success',
  marketingAttributionModelDeploySuccess = 'campaign_attribution_model:deploy_success',
  fetchDoneInSession = 'fetch_done_in_session',
  sfSandboxConnect = 'sf_sandbox_connect',
  sfProdConnect = 'sf_prod_connect',
  devopsCenterDeploySuccess = 'devops_center:deploy_success',
  scheduledReportsAdd = 'scheduled_reports:add_scheduled_reports',
  scheduledJobsAdd = 'scheduled_jobs:add_scheduled_jobs',
  scheduledReportsDeploySuccess = 'scheduled_reports:deploy_success',
  scheduledJobsDeploySuccess = 'scheduled_jobs:deploy_success',
  removeEnv = 'remove_env',
  assignSweepPsg = 'assign_sweep_psg', // psg => "permission set group"
  automationsDelete = 'automations:delete_automation',
  marketingAttributionModelDelete = 'campaign_attribution_model:delete_model',
  alertsDelete = 'alerts:delete_alerts',
  assignmentsDelete = 'assignments:delete_assignment',
  matchingDedupeDelete = 'matching_dedupe:delete_dm',
  scheduledAssignmentsDelete = 'scheduled_assignments:delete_scheduled_assignment',
  scheduledReportsDelete = 'scheduled_reports:delete_scheduled_reports',
  scheduledJobsDelete = 'scheduled_jobs:delete_scheduled_jobs',
  assignmentGroupsDelete = 'assignment_groups:delete_group',
  gatesDelete = 'gates:delete_gate',
  territoriesDelete = 'territories:delete_territory',
  salesforceCampaignDeploySuccess = 'salesforce_campaign:deploy_success',
  customIntegrationAdd = 'integrations:add_custom',

  // Hubspot events
  hubspotConnect = 'integrations:connect_hubspot',
  hubspotEnrollmentRulDeploySuccess = 'hubspot_enrollment_rule:deploy_success',
  hubspotAddFieldToCampaignForm = 'hubspot:add_field_to_campaign_form',
  hubspotDuplicateCampaignForm = 'hubspot:duplicate_campaign_form',
  hubspotAddNewCampaignMemberStatus = 'hubspot:add_new_campaign_member_status',
}

export enum GET_STARTED_EVENTS {
  documentationTakeTour = 'get_started:launch_documentation_tour',
  documentationNavigate = 'get_started:documentation_navigate',
  automationsTakeTour = 'get_started:launch_automations_tour',
  automationsNavigate = 'get_started:automations_navigate',
  slackTakeTour = 'get_started:launch_slack_alerts_tour',
  slackNavigate = 'get_started:slack_alerts_navigate',
  rollupsTakeTour = 'get_started:launch_rollups_tour',
  rollupsNavigate = 'get_started:rollups_navigate',
  assignmentTakeTour = 'get_started:launch_assignment_tour',
  assignmentNavigate = 'get_started:assignment_navigate',
  dedupeTakeTour = 'get_started:launch_dedupe_tour',
  dedupeNavigate = 'get_started:dedupe_navigate',
  visualizeTakeTour = 'get_started:launch_visualize_tour',
  visualizeNavigate = 'get_started:visualize_navigate',
  optimizeTakeTour = 'get_started:launch_optimize_tour',
  optimizeNavigate = 'get_started:optimize_navigate',
  changeFeedTakeTour = 'get_started:launch_change_feed_tour',
  changeFeedNavigate = 'get_started:change_feed_navigate',
  sfSandboxConnect = 'get_started:sf_sandbox_connect',
  sfProdConnect = 'get_started:sf_prod_connect',
}

export enum HOMEPAGE_EVENTS {
  connectSf = 'homepage:connect-salesforce',
  exploreWithAi = 'homepage:explore-with-ai',
  viewAllDocumentation = 'homepage:discover-full-org-documentation',
  getStartedChangeFeed = 'homepage:get-started-change-feed',
  getStartedRollups = 'homepage:get-started-rollups',
  getStartedFunnels = 'homepage:get-started-funnels',
  upgradeRouting = 'homepage:upgrade-routing',
  upgradeAlerts = 'homepage:upgrade-alerts',
  upgradeDedupeAndMatching = 'homepage:upgrade-dedupe-matching',
  upgradeAutomations = 'homepage:upgrade-automations',
  upgradeFreeQuestions = 'homepage:upgrade-free-questions',
}

export enum SIGN_UP_EVENTS {
  newUserLogin = 'signup:new_user_login',
  accountCreated = 'signup:account_created',
  sfSandboxConnect = 'signup:sf_sandbox_connect',
  sfProdConnect = 'signup:sf_prod_connect',
}

export enum PAGE_VISIT_EVENTS {
  funnels = 'visit_pages:visit_funnels',
  documentation = 'visit_pages:visit_documentation',
  changeFeed = 'visit_pages:visit_change_feed',
  rollups = 'visit_pages:visit_rollups',
  automations = 'visit_pages:visit_automations',
  alerts = 'visit_pages:visit_alerts',
  routing = 'visit_pages:visit_routing',
  reports = 'visit_pages:visit_reporting',
  devopsCenter = 'visit_pages:visit_devops_center',
  aiAssistant = 'visit_pages:ai_assistant',
  marketingManagement = 'visit_pages:marketing_attribution',
  dedup = 'visit_pages:dedup',
  agentic = 'visit_pages:agentic',
}

export enum AI_AGENTS_EVENTS {
  newAgentClick = 'ai_agents:new_agent_click',
  newChatClick = 'ai_agents:new_chat_click',
  openAgentCenter = 'ai_agents:open_agent_center',
  drawPolygon = 'ai_agents:draw_polygon',
  activateAgent = 'ai_agents:activate_agent',
  createAgent = 'ai_agents:create_agent',
  createNewChat = 'ai_agents:create_new_chat',
  expand = 'ai_agents:expand',
  deleteAgent = 'ai_agents:delete_agent',
  editInstructions = 'ai_agents:edit_instructions',
  addFiles = 'ai_agents:add_files',
  shareChatCopyLink = 'ai_agents:share_chat_copy_link',
  viewSharedChat = 'ai_agents:view_shared_chat',
  exportToPdfFromMenu = 'ai_agents:export_to_pdf_from_menu',
  exportToPdfFromChat = 'ai_agents:export_to_pdf_from_chat',
  copyMessageText = 'ai_agents:copy_message_text',
  renameChat = 'ai_agents:rename_chat',
  starChat = 'ai_agents:star_chat',
  promptsLibraryOpen = 'ai_agents:prompts_library_open',
  promptsLibraryNewCategory = 'ai_agents:prompts_library_new_category',
  promptsLibraryPinCategory = 'ai_agents:prompts_library_pin_category',
  promptsLibraryNewPromptClick = 'ai_agents:prompts_library_new_prompt_click',
  promptsLibraryPromptClick = 'ai_agents:prompts_library_prompt_click',
  promptsLibraryPinnedPromptCategoryClick = 'ai_agents:prompts_library_pinned_prompt_category_click',
  promptsLibraryPinnedPromptClick = 'ai_agents:prompts_library_pinned_prompt_click',
  monitoringWidgetActivateAgent = 'ai_agents:monitoring_widget_activate_agent',
  monitoringWidgetCreateNewAgent = 'ai_agents:monitoring_widget_create_new_agent',
  generateProcessMiningFromHomepage = 'ai_agents:generate_process_mining_from_homepage',
  generateProcessMiningFromCanvas = 'ai_agents:generate_process_mining_from_canvas',
  monitoringAgentNewBestPractice = 'ai_agents:monitoring_agent_new_best_practice',
  monitoringAgentConfigurationChange = 'ai_agents:monitoring_agent_configuration_change',
  newQuestion = 'ai_agents:new_question',
}

export enum MARKETING_ATTRIBUTION_EVENTS {
  createModel = 'marketing_attribution:create_model',
  deleteModel = 'marketing_attribution:delete_model',
  deployModel = 'marketing_attribution:deploy_model',
  viewInSalesforce = 'marketing_attribution:view_in_salesforce',
  activate = 'marketing_attribution:activate',
}
